import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import Dialog from '../../../../../components/Dialog';
import Text from '../../../../../components/Text';
import StyleInput from '../../../../../components/StyleInput';
import { AddIcon, DeleteIcon } from '../../../../../components/Icon';
import { getUUID } from '../../../../../utils';
import { i18n } from '../../../../../language';

const useStyles = makeStyles(() => ({
  deleteIcon: {
    fontSize: '1.5rem',
    color: '#538FFF',
  },
  addIcon: {
    fontSize: '1.125rem',
    color: '#538FFF',
  },
}));

const RecipientsListPopup = ({ handleClose, recipients = [], handleSubmit }) => {
  const classes = useStyles();
  const getEmptyRecipients = () => ({
    categoryRecipientsId: getUUID(),
    recipients: '',
    recipientsType: '',
    isNew: true,
  });
  const getExistingRecipient = (recipientsType) => {
    const recipientList = _.filter(recipients, (item) => item?.recipientsType === recipientsType) ?? [];
    return recipientList?.length !== 0 ? recipientList : [{ ...getEmptyRecipients(), recipientsType }];
  };
  const [formData, setFormData] = useState({
    email: getExistingRecipient('email'),
    hachat: getExistingRecipient('hachat'),
  });
  const confirmDisabled = useMemo(() => {
    if (
      _.some(formData?.email, (emailItem) => emailItem?.recipients) ||
      _.some(formData?.hachat, (haChatItem) => haChatItem?.recipients)
    ) {
      return false;
    }

    return true;
  }, [formData]);

  const handleAdd = (type) => () => {
    setFormData((prevState) => ({
      ...prevState,
      [type]: [...prevState[type], { ...getEmptyRecipients(), recipientsType: type }],
    }));
  };

  const handleDelete = (id, type) => () => {
    setFormData((prevState) => ({
      ...prevState,
      [type]: prevState[type]?.filter((item) => item.categoryRecipientsId !== id),
    }));
  };

  const handleUpdate = (id, type) => (value) => {
    setFormData((prevState) => ({
      ...prevState,
      [type]: prevState[type]?.map((item) =>
        item.categoryRecipientsId === id ? { ...item, recipients: value } : item
      ),
    }));
  };

  const confirmAction = () => {
    handleClose();
    const emailRecipients = _.filter(formData?.email, (item) => item?.recipients);
    const hachatRecipients = _.filter(formData?.hachat, (item) => item?.recipients);
    handleSubmit(emailRecipients?.concat(hachatRecipients));
  };

  return (
    <Dialog
      open={true}
      title={i18n.t('SETTING_EQUIPMENT_EDIT_RECIPIENTS_LISTS')}
      cancel="Cancel"
      confirm="Confirm"
      dialogfootStyle={{ marginTop: '0', paddingTop: '1rem', borderTop: '1px solid #E6E9F2' }}
      width="auto"
      handleClose={handleClose}
      handleCancel={handleClose}
      handleConfirm={confirmAction}
      disabled={confirmDisabled}
    >
      <Box display="flex" width="100%" mb="1.56rem" gridGap="2.5rem">
        <Box flexBasis="50%">
          <Box>
            <Text color="#2E4469" fontSize="1.5rem" fontWeight="700" mb="1rem">
              {i18n.t('SETTING_EQUIPMENT_EMAIL_ADDRESS')}
            </Text>
            <Text color="#2E4469" fontSize="1rem" mb="0.75rem">
              Recipients Email address
            </Text>
            <Box display="flex" flexDirection="column" gridGap="0.62rem">
              {formData.email.map((emailItem, index) => (
                <Box
                  display="flex"
                  alignItems="center"
                  gridGap="0.62rem"
                  key={`email-${index}-${emailItem.categoryRecipientsId}`}
                >
                  <StyleInput
                    width="25rem"
                    value={emailItem.recipients}
                    onChange={handleUpdate(emailItem.categoryRecipientsId, 'email')}
                  />
                  <ButtonBase onClick={handleDelete(emailItem.categoryRecipientsId, 'email')}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="2.25rem"
                      height="2.25rem"
                      bgcolor="#F5FAFF"
                      borderRadius="0.5rem"
                      border="1px solid #538fff"
                    >
                      <DeleteIcon className={classes.deleteIcon} />
                    </Box>
                  </ButtonBase>
                </Box>
              ))}
              <Box display="flex">
                <ButtonBase onClick={handleAdd('email')}>
                  <AddIcon className={classes.addIcon} />
                  <Text color="#538FFF" fontSize="1rem" fontWeight="700" ml="0.69rem">
                    Add Email Address
                  </Text>
                </ButtonBase>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box minWidth="1px" bgcolor="#E6E7E8" />
        <Box flexBasis="50%">
          <Box>
            <Text color="#2E4469" fontSize="1.5rem" fontWeight="700" mb="1rem">
              HA Chat (based on CORP ID)
            </Text>
            <Text color="#2E4469" fontSize="1rem" mb="0.75rem">
              Recipients HA Chat (based on CORP ID)
            </Text>
            <Box display="flex" flexDirection="column" gridGap="0.62rem">
              {formData.hachat.map((haChatItem, index) => (
                <Box
                  display="flex"
                  alignItems="center"
                  gridGap="0.62rem"
                  key={`hachat-${index}-${haChatItem.categoryRecipientsId}`}
                >
                  <StyleInput
                    width="25rem"
                    value={haChatItem.recipients}
                    onChange={handleUpdate(haChatItem.categoryRecipientsId, 'hachat')}
                  />
                  <ButtonBase onClick={handleDelete(haChatItem.categoryRecipientsId, 'hachat')}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="2.25rem"
                      height="2.25rem"
                      bgcolor="#F5FAFF"
                      borderRadius="0.5rem"
                      border="1px solid #538fff"
                    >
                      <DeleteIcon className={classes.deleteIcon} />
                    </Box>
                  </ButtonBase>
                </Box>
              ))}
              <Box display="flex">
                <ButtonBase onClick={handleAdd('hachat')}>
                  <AddIcon className={classes.addIcon} />
                  <Text color="#538FFF" fontSize="1rem" fontWeight="700" ml="0.69rem">
                    Add HA Chat (based on CORP ID)
                  </Text>
                </ButtonBase>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RecipientsListPopup;
